/**
 * ChatPage — Individual conversation view with real-time messaging
 *
 * Adapted from bachata-nation-next Chat.tsx (2164L).
 * Real-time WebSocket messaging, typing indicators, reactions, media,
 * reply threading, optimistic updates, offline caching. Neubrutalism UI.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '@/features/auth';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import {
  useConversation,
  useMessages,
  useAddMessageToCache,
  useUpdateMessageInCache,
  useMarkConversationReadInCache,
  useMarkMessagesReadInCache,
  getConversationDisplayName,
  getConversationAvatar,
  messagingKeys,
} from '../hooks';
import { useChat } from '../hooks';
import { useOptimisticMessages } from '../hooks';
import { useCachedMessages } from '../hooks';
import { usePresence } from '../hooks';
import { useTypingStore, useConversationTyping } from '../hooks';
import { useMessagingFlow } from '@/stackflow';
import { registerMessagingFlow } from '../messagingNavBridge';
import { PageLayout } from '@/components/layout';
import {
  ChatAvatar,
  MessageBubble,
  TypingIndicator,
  MessageActionMenu,
  VoiceRecorder,
  EmojiPicker,
  MediaPicker,
  ForwardMessage,
  MessageSearch,
  PollCreator,
  UserProfileSheet,
  MessageInfoModal,
  ConnectionStatusBanner,
  MediaViewer,
  VidnightBackground,
  ThreadPanel,
  MentionAutocomplete,
  ChatPageSkeleton,
} from '../components';
import { UserProfileModal } from '@/features/feed/components/UserProfileModal';
import { useUpload, useForwardMessage } from '../hooks';
import { useMediaGallery } from '../hooks/useMediaGallery';
import type { VoiceRecorderResult } from '../hooks/useVoiceRecorder';
import type { Conversation, ChatMessage, WsMessageEvent, WsTypingEvent, WsReadEvent, WsReactionEvent, WsDeleteEvent, WsEditEvent, WsPresenceEvent, WsPinEvent, WsThreadUpdateEvent, Mention } from '../types';
import { generateVideoThumbnail } from '../utils/generateVideoThumbnail';
import { isSameDay } from '../utils/dateUtils';
import { chatHaptic } from '../utils/chatHaptic';
import { DateSeparator } from '../components/DateSeparator';

// =============================================================================
// Message Input
// =============================================================================

interface MessageInputProps {
  onSend: (content: string, mentions?: Mention[]) => void;
  onTyping: (isTyping: boolean) => void;
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
  disabled?: boolean;
  onMicPress?: () => void;
  onAttachPress?: () => void;
  isMediaPickerOpen?: boolean;
  onMediaPickerClose?: () => void;
  onMediaFilesSelected?: (files: File[]) => void;
  onEmojiSelect?: (emoji: string) => void;
  participants?: import('../types').ConversationParticipant[];
  conversationType?: import('../types').ConversationType;
  currentUserId?: string;
  conversationId?: string;
}

const MessageInput = memo(function MessageInput({
  onSend,
  onTyping,
  replyTo,
  onCancelReply,
  disabled,
  onMicPress,
  onAttachPress,
  isMediaPickerOpen,
  onMediaPickerClose,
  onMediaFilesSelected,
  onEmojiSelect: _onEmojiSelect,
  participants,
  conversationType,
  currentUserId: inputCurrentUserId,
  conversationId,
}: MessageInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const mentionsRef = useRef<Mention[]>([]);

  // Auto-focus textarea on mount and when conversation changes
  useEffect(() => {
    if (!conversationId) return;
    const attempts = [50, 200, 500].map((ms) =>
      setTimeout(() => textareaRef.current?.focus(), ms)
    );
    return () => attempts.forEach(clearTimeout);
  }, [conversationId]);

  // Handle emoji insertion from picker
  const handleEmojiInsert = useCallback((emoji: string) => {
    setText((prev) => prev + emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  }, []);

  const handleInput = useCallback(
    (value: string) => {
      setText(value);

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }

      // Detect @ for mention autocomplete
      if (textareaRef.current) {
        const cursorPos = textareaRef.current.selectionStart;
        const textBeforeCursor = value.slice(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@(\w*)$/);
        if (atMatch) {
          setMentionQuery(atMatch[1]);
        } else {
          setMentionQuery(null);
        }
      }

      // Typing indicator
      if (!isTypingRef.current && value.trim()) {
        isTypingRef.current = true;
        onTyping(true);
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        onTyping(false);
      }, 2000);
    },
    [onTyping]
  );

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;

    onSend(trimmed, mentionsRef.current.length > 0 ? [...mentionsRef.current] : undefined);
    setText('');
    mentionsRef.current = [];
    setMentionQuery(null);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Stop typing
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTyping(false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [text, onSend, onTyping]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleMentionSelect = useCallback((mention: Mention, displayName: string) => {
    mentionsRef.current.push(mention);
    // Replace @query with @DisplayName in text
    if (textareaRef.current) {
      const cursorPos = textareaRef.current.selectionStart;
      const textBefore = text.slice(0, cursorPos);
      const textAfter = text.slice(cursorPos);
      const atMatch = textBefore.match(/@(\w*)$/);
      if (atMatch) {
        const newText = textBefore.slice(0, atMatch.index) + `@${displayName} ` + textAfter;
        setText(newText);
      }
    }
    setMentionQuery(null);
  }, [text]);

  return (
    <div className="relative flex-shrink-0 border-t glass-surface-nav px-3 pt-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))', borderColor: 'var(--user-page-card-border)' }}>
      {/* Mention autocomplete */}
      {mentionQuery !== null && participants && conversationType && inputCurrentUserId && (
        <MentionAutocomplete
          query={mentionQuery}
          participants={participants}
          conversationType={conversationType}
          currentUserId={inputCurrentUserId}
          onSelect={handleMentionSelect}
          onClose={() => setMentionQuery(null)}
        />
      )}

      {/* Reply preview */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-2 overflow-hidden"
          >
            <div className="flex items-center gap-2 rounded-lg border-s-[3px] px-3 py-2" style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}>
              <div className="flex-1 overflow-hidden" dir="auto">
                <div className="text-xs font-medium" style={{ color: 'var(--user-page-heading)' }}>{replyTo.sender.name}</div>
                <div className="truncate text-xs" style={{ color: 'var(--user-page-text)' }}>
                  {replyTo.content || `[${replyTo.type}]`}
                </div>
              </div>
              <button
                type="button"
                onClick={onCancelReply}
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full hover:opacity-80"
                style={{ backgroundColor: 'var(--user-page-card-border)', color: 'var(--user-page-heading)' }}
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input row */}
      <div className="flex items-end gap-2">
        {/* Attachment button + MediaPicker dropdown */}
        {onAttachPress && (
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={onAttachPress}
              disabled={disabled}
              className="flex h-10 w-10 items-center justify-center rounded-xl border transition-all hover:opacity-80 active:scale-95 disabled:opacity-40"
              style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            {isMediaPickerOpen && onMediaPickerClose && onMediaFilesSelected && (
              <MediaPicker
                isOpen={isMediaPickerOpen}
                onClose={onMediaPickerClose}
                onFilesSelected={onMediaFilesSelected}
              />
            )}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('messaging.typeMessage')}
          disabled={disabled}
          autoFocus
          rows={2}
          dir="auto"
          className="flex-1 resize-none rounded-xl border px-3 py-2 text-sm placeholder:opacity-70 transition-all focus:shadow-sm focus:outline-none disabled:opacity-50"
          style={{ maxHeight: 120, borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
        />
        {/* Emoji button */}
        <button
          type="button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          disabled={disabled}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border transition-all hover:opacity-80 active:scale-95 disabled:opacity-40"
          style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </button>
        {text.trim() ? (
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border text-white shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-sm active:translate-y-[1px] active:shadow-none disabled:opacity-40 disabled:shadow-none"
            style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-heading)' }}
          >
            <svg className="h-5 w-5 -rotate-45 rtl:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onPointerDown={onMicPress}
            disabled={disabled}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-sm active:translate-y-[1px] active:shadow-none disabled:opacity-40 disabled:shadow-none"
            style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
          >
            {/* Microphone icon */}
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
        )}
      </div>
      {/* Inline Emoji Picker */}
      <EmojiPicker
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onEmojiSelect={handleEmojiInsert}
        bottomOffset={0}
      />
    </div>
  );
});

// =============================================================================
// Main ChatPage
// =============================================================================

export function ChatPage({ conversationId: propConversationId, scrollToMessageId: propScrollTo }: { conversationId?: string; scrollToMessageId?: string }) {
  const params = useParams<{ conversationId: string }>();
  const conversationId = propConversationId || params.conversationId;
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollToMessageId = propScrollTo || searchParams.get('scrollTo');
  const { pop, push, replace } = useMessagingFlow();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id) || '';
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  // Register Stackflow navigation bridge for desktop left pane
  useEffect(() => {
    if (!isMobile) {
      registerMessagingFlow({ push, replace });
    }
  }, [isMobile, push, replace]);

  // State
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [forwardMessage, setForwardMessage] = useState<ChatMessage | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPollCreatorOpen, setIsPollCreatorOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<{ id: string; name: string; avatar?: string | null; role?: string } | null>(null);
  const [viewProfileUserId, setViewProfileUserId] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<ChatMessage | null>(null);
  const typingUsers = useConversationTyping(conversationId || null);
  const { setTyping, clearTyping } = useTypingStore();
  const [actionMenu, setActionMenu] = useState<{
    message: ChatMessage;
    position: { x: number; y: number };
  } | null>(null);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const [activeThread, setActiveThread] = useState<ChatMessage | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollToHandledRef = useRef<string | null>(null);

  // Data queries
  const { data: conversation } = useConversation(conversationId || null);
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMessages(conversationId || null);

  // Flatten infinite query pages
  const serverMessages = useMemo(() => {
    if (!messagesData?.pages) return [];
    return messagesData.pages.flatMap((page) => page.messages);
  }, [messagesData]);

  // Cache helpers
  const addMessageToCache = useAddMessageToCache();
  const updateMessageInCache = useUpdateMessageInCache();
  const markConversationRead = useMarkConversationReadInCache();
  const markMessagesRead = useMarkMessagesReadInCache();

  // IndexedDB cache
  const { cachedMessages, isFresh, cacheServerMessages } = useCachedMessages({
    conversationId: conversationId || '',
    enabled: !!conversationId,
  });

  // Use cached messages if server hasn't loaded yet
  const baseMessages = serverMessages.length > 0 ? serverMessages : (isFresh ? cachedMessages : []);

  // Cache server messages when they arrive
  useEffect(() => {
    if (serverMessages.length > 0) {
      cacheServerMessages(serverMessages);
    }
  }, [serverMessages, cacheServerMessages]);

  // Send message via REST (used by optimistic hook)
  const sendMessage = useCallback(
    async (_params: { content: string; type?: string; replyToId?: string }) => {
      if (!conversationId) return;
    },
    [conversationId]
  );

  // Optimistic messages
  const {
    optimisticMessages,
    sendOptimistic,
    retrySend,
    removeOptimistic,
  } = useOptimisticMessages({
    messages: baseMessages,
    conversationId: conversationId || '',
    sendMessage,
  });

  // Media gallery for lightbox viewer
  const { items: mediaGallery, findIndex: findMediaIndex } = useMediaGallery(optimisticMessages);

  const handleMediaTap = useCallback((messageId: string) => {
    const idx = findMediaIndex(messageId);
    if (idx >= 0) setViewerIndex(idx);
  }, [findMediaIndex]);

  // Presence (DM partner)
  const dmPartnerId = useMemo(() => {
    if (!conversation || conversation.type !== 'dm') return null;
    const partner = conversation.participants.find((p) => p.userId !== currentUserId);
    return partner?.userId || null;
  }, [conversation, currentUserId]);

  const { isOnline, lastSeenFormatted, updateFromWebSocket: updatePresence } = usePresence(dmPartnerId);

  // Display info
  const displayName = conversation
    ? getConversationDisplayName(conversation, currentUserId)
    : '';
  const displayAvatar = conversation
    ? getConversationAvatar(conversation, currentUserId)
    : null;

  // =========================================================================
  // WebSocket handlers
  // =========================================================================

  const handleWsMessage = useCallback(
    (event: WsMessageEvent) => {
      if (!conversationId) return;

      const newMessage: ChatMessage = {
        id: event.id,
        conversationId,
        senderId: event.senderId,
        sequenceId: event.sequenceId,
        type: event.messageType,
        content: event.content || null,
        mediaUrl: event.mediaUrl || null,
        mediaThumbnail: event.mediaThumbnail || null,
        mediaMetadata: (event.mediaMetadata as ChatMessage['mediaMetadata']) || null,
        replyTo: event.replyTo || null,
        mentions: event.mentions || null,
        forwardedFrom: event.forwardedFrom || null,
        threadRootId: event.threadRootId || null,
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        createdAt: new Date(event.timestamp).toISOString(),
        sender: {
          id: event.senderId,
          name: event.senderName,
          avatar: event.senderAvatar,
        },
        reactions: {},
        isStarred: false,
      };

      addMessageToCache(conversationId, newMessage);

      // Haptic feedback for incoming messages from others
      if (event.senderId !== currentUserId) {
        chatHaptic.receive();
      }
    },
    [conversationId, addMessageToCache, currentUserId]
  );

  const handleWsTyping = useCallback(
    (event: WsTypingEvent) => {
      if (event.userId === currentUserId || !conversationId) return;

      if (event.isTyping) {
        setTyping(conversationId, { userId: event.userId, userName: event.userName, userAvatar: event.userAvatar });
      } else {
        clearTyping(conversationId, event.userId);
      }
    },
    [currentUserId, conversationId, setTyping, clearTyping]
  );

  const handleWsReaction = useCallback(
    (event: WsReactionEvent) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.messageId, (msg) => {
        const reactions = { ...msg.reactions };
        const users = reactions[event.emoji] ? [...reactions[event.emoji]] : [];
        if (event.action === 'add' && !users.includes(event.userId)) {
          users.push(event.userId);
        } else if (event.action === 'remove') {
          const idx = users.indexOf(event.userId);
          if (idx >= 0) users.splice(idx, 1);
        }
        reactions[event.emoji] = users;
        if (users.length === 0) delete reactions[event.emoji];
        return { ...msg, reactions };
      });
    },
    [conversationId, updateMessageInCache]
  );

  const handleWsDelete = useCallback(
    (event: WsDeleteEvent) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        isDeleted: true,
        content: null,
      }));
    },
    [conversationId, updateMessageInCache]
  );

  const handleWsEdit = useCallback(
    (event: WsEditEvent) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        content: event.content,
        isEdited: true,
        editedAt: new Date(event.editedAt).toISOString(),
      }));
    },
    [conversationId, updateMessageInCache]
  );

  const handleWsPresence = useCallback(
    (event: WsPresenceEvent) => {
      if (event.userId === dmPartnerId) {
        updatePresence({ status: event.status, lastSeen: event.lastSeen });
      }
    },
    [dmPartnerId, updatePresence]
  );

  const handleWsRead = useCallback(
    (event: WsReadEvent) => {
      if (!conversationId) return;
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        markMessagesRead(conversationId, event.upToSequenceId, userId);
      }
    },
    [conversationId, markMessagesRead]
  );

  // Star handler
  const handleWsStar = useCallback(
    (event: { messageId: string; userId: string; action: 'add' | 'remove' }) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        isStarred: event.action === 'add',
      }));
      queryClient.invalidateQueries({ queryKey: messagingKeys.starred(conversationId) });
      queryClient.invalidateQueries({ queryKey: messagingKeys.globalStarred() });
    },
    [conversationId, updateMessageInCache, queryClient]
  );

  // Pin handler
  const handleWsPin = useCallback(
    (event: WsPinEvent) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        isPinned: event.action === 'pin',
      }));
    },
    [conversationId, updateMessageInCache]
  );

  // Thread update handler
  const handleWsThreadUpdate = useCallback(
    (event: WsThreadUpdateEvent) => {
      if (!conversationId) return;
      updateMessageInCache(conversationId, event.threadRootId, (msg) => ({
        ...msg,
        replyCount: (msg.replyCount || 0) + 1,
        lastReplyAt: new Date(event.lastReplyAt).toISOString(),
        lastReplyPreview: event.lastReplyPreview,
        lastReplySenderId: event.lastReplySenderId,
        lastReplySenderName: event.lastReplySenderName,
      }));
    },
    [conversationId, updateMessageInCache]
  );

  // Upload hook for media/voice
  const { upload: uploadFile } = useUpload();

  // Connect WebSocket
  const { isConnected, sendText, sendMedia, sendTyping, sendReaction, sendDelete, sendEdit, sendRead, sendStar, sendPin, sendPoll } =
    useChat({
      conversationId: conversationId || null,
      enabled: !!conversationId,
      onMessage: handleWsMessage,
      onTyping: handleWsTyping,
      onRead: handleWsRead,
      onReaction: handleWsReaction,
      onDelete: handleWsDelete,
      onEdit: handleWsEdit,
      onPresence: handleWsPresence,
      onStar: handleWsStar,
      onPin: handleWsPin,
      onThreadUpdate: handleWsThreadUpdate,
    });

  // =========================================================================
  // Scroll management
  // =========================================================================

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, []);

  // Scroll to bottom on new messages (skip while scrollTo target is pending)
  useEffect(() => {
    if (scrollToMessageId && scrollToHandledRef.current !== scrollToMessageId) return;
    scrollToBottom(true);
  }, [optimisticMessages.length, scrollToBottom, scrollToMessageId]);

  // Initial scroll to bottom (skip if we have a scrollTo target)
  useEffect(() => {
    if (!scrollToMessageId) {
      scrollToBottom(false);
    }
  }, [conversationId, scrollToBottom, scrollToMessageId]);

  // Scroll to specific message (from starred message tap or notification click)
  useEffect(() => {
    if (!scrollToMessageId || serverMessages.length === 0) return;
    // Prevent re-triggering for the same message (prop-based scrollTo can't be cleared)
    if (scrollToHandledRef.current === scrollToMessageId) return;

    // Small delay to let the DOM render
    const timer = setTimeout(() => {
      const el = document.getElementById(`msg-${scrollToMessageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessageId(scrollToMessageId);
        setTimeout(() => setHighlightedMessageId(null), 3000);
        scrollToHandledRef.current = scrollToMessageId;
        // Clear URL param if present
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('scrollTo');
          return next;
        }, { replace: true });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [scrollToMessageId, serverMessages.length, setSearchParams]);

  // Mark messages as read
  useEffect(() => {
    if (!conversationId || !isConnected || serverMessages.length === 0) return;
    const lastMsg = serverMessages[serverMessages.length - 1];
    if (lastMsg && lastMsg.sequenceId > 0) {
      sendRead(lastMsg.sequenceId);
      // Optimistically clear unread badge — don't wait for server refetch
      markConversationRead(conversationId, lastMsg.sequenceId);
    }
  }, [conversationId, isConnected, serverMessages, sendRead, markConversationRead]);

  // Load more on scroll to top
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (container.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // =========================================================================
  // Message actions
  // =========================================================================

  const handleSendMessage = useCallback(
    (content: string, mentions?: Mention[]) => {
      chatHaptic.send();
      sendText(content, replyTo?.id, mentions);
      sendOptimistic({
        content,
        replyToId: replyTo?.id,
        replyTo: replyTo
          ? {
              id: replyTo.id,
              content: replyTo.content,
              type: replyTo.type,
              senderId: replyTo.senderId,
              senderName: replyTo.sender.name,
            }
          : undefined,
      });
      setReplyTo(null);
    },
    [sendText, sendOptimistic, replyTo]
  );

  const handleReaction = useCallback(
    (messageId: string, emoji: string, action: 'add' | 'remove') => {
      sendReaction(messageId, emoji, action);
    },
    [sendReaction]
  );

  const handleDelete = useCallback(
    (messageId: string) => {
      sendDelete(messageId);
    },
    [sendDelete]
  );

  const handleEdit = useCallback(
    (messageId: string, content: string) => {
      sendEdit(messageId, content);
    },
    [sendEdit]
  );

  const handleStar = useCallback(
    (messageId: string, action: 'add' | 'remove') => {
      // Optimistic update — toggle star immediately in cache
      if (conversationId) {
        updateMessageInCache(conversationId, messageId, (msg) => ({
          ...msg,
          isStarred: action === 'add',
        }));
        queryClient.invalidateQueries({ queryKey: messagingKeys.starred(conversationId) });
        queryClient.invalidateQueries({ queryKey: messagingKeys.globalStarred() });
      }
      sendStar(messageId, action);
    },
    [conversationId, updateMessageInCache, sendStar, queryClient]
  );

  const handleOpenThread = useCallback(
    (message: ChatMessage) => {
      if (isMobile && conversationId) {
        push('ThreadActivity', { conversationId, messageId: message.id });
      } else {
        setActiveThread(message);
      }
    },
    [isMobile, conversationId, push]
  );

  const handleReply = useCallback((message: ChatMessage) => {
    setReplyTo(message);
    setActionMenu(null);
  }, []);

  const handleVoiceRecordingComplete = useCallback(
    async (result: VoiceRecorderResult) => {
      setIsRecordingVoice(false);

      // Upload voice recording to R2
      const ext = result.mimeType.includes('webm') ? 'webm' : result.mimeType.includes('mp4') ? 'mp4' : 'ogg';
      const file = new File([result.blob], `voice-${Date.now()}.${ext}`, { type: result.mimeType });
      const uploadResult = await uploadFile(file, 'message');

      if (uploadResult) {
        sendMedia('audio', uploadResult.url, undefined, {
          duration: result.duration,
          mimeType: result.mimeType,
          fileSize: result.blob.size,
          fileName: file.name,
          waveformPeaks: result.waveformPeaks,
        });
      }
    },
    [uploadFile, sendMedia]
  );

  const handleLongPress = useCallback(
    (message: ChatMessage, e: React.MouseEvent | React.TouchEvent) => {
      const clientX = 'clientX' in e ? e.clientX : e.touches?.[0]?.clientX || 0;
      const clientY = 'clientY' in e ? e.clientY : e.touches?.[0]?.clientY || 0;
      setActionMenu({ message, position: { x: clientX, y: clientY } });
    },
    []
  );

  // Forward message
  const forwardMutation = useForwardMessage();
  const handleForward = useCallback(
    (conversationIds: string[], additionalMessage?: string) => {
      if (!forwardMessage) return;
      forwardMutation.mutate({
        messageId: forwardMessage.id,
        conversationIds,
        additionalMessage,
      });
      setForwardMessage(null);
    },
    [forwardMessage, forwardMutation]
  );

  // Media upload handler
  const handleMediaFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const uploadResult = await uploadFile(file, 'message');
        if (uploadResult) {
          const mediaType = file.type.startsWith('image/')
            ? 'image' as const
            : file.type.startsWith('video/')
            ? 'video' as const
            : 'file' as const;

          // Generate and upload video thumbnail
          let mediaThumbnail: string | undefined;
          if (mediaType === 'video') {
            const thumbBlob = await generateVideoThumbnail(file);
            if (thumbBlob) {
              const thumbFile = new File([thumbBlob], `thumb-${file.name}.webp`, { type: 'image/webp' });
              const thumbResult = await uploadFile(thumbFile, 'message');
              mediaThumbnail = thumbResult?.url;
            }
          }

          sendMedia(mediaType, uploadResult.url, mediaThumbnail, {
            mimeType: file.type,
            fileSize: file.size,
            fileName: file.name,
          });
        }
      }
    },
    [uploadFile, sendMedia]
  );

  // Poll creation handler
  const handleCreatePoll = useCallback(
    (question: string, options: string[]) => {
      sendPoll(question, options);
    },
    [sendPoll]
  );

  // Search result select handler
  const handleSearchResultSelect = useCallback(
    (messageId: string) => {
      const el = document.getElementById(`msg-${messageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessageId(messageId);
        setTimeout(() => setHighlightedMessageId(null), 3000);
      }
    },
    []
  );

  // Top bar avatar tap — show user profile (DM) or channel info (group/channel)
  const handleAvatarTap = useCallback(() => {
    if (!conversation) return;
    if (conversation.type === 'dm') {
      const partner = conversation.participants.find((p) => p.userId !== currentUserId);
      if (partner) {
        setProfileUser({
          id: partner.userId,
          name: partner.user.name,
          avatar: partner.user.avatar,
          role: partner.user.role,
        });
      }
    } else {
      push('ChannelInfoActivity', { conversationId: conversation.id });
    }
  }, [conversation, currentUserId, push]);


  // =========================================================================
  // Message grouping
  // =========================================================================

  const groupedMessages = useMemo(() => {
    return optimisticMessages.map((msg, i) => {
      const prev = i > 0 ? optimisticMessages[i - 1] : null;
      const sameSender = prev?.senderId === msg.senderId;
      const withinTime =
        prev &&
        Math.abs(new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime()) <
          5 * 60 * 1000;
      const isGrouped = sameSender && withinTime;
      const showSender = !sameSender && msg.type !== 'system';
      const showAvatar = showSender;

      return { msg, isGrouped: !!isGrouped, showSender, showAvatar };
    });
  }, [optimisticMessages]);

  // Mini read-receipt avatar — find last own message that peer has read (DM only)
  const lastReadMessageId = useMemo(() => {
    if (!conversation || conversation.type !== 'dm') return null;
    for (let i = optimisticMessages.length - 1; i >= 0; i--) {
      const msg = optimisticMessages[i];
      if (msg.senderId === currentUserId && msg.isRead) {
        return msg.id;
      }
    }
    return null;
  }, [optimisticMessages, currentUserId, conversation]);

  // State carry-over: while the full conversation query loads, "borrow"
  // name + avatar from the cached conversation list so the header is never empty.
  const preview = useMemo(() => {
    if (conversation) return null; // already loaded, no need for preview
    const cached = queryClient.getQueryData<{ conversations: Conversation[] }>(
      messagingKeys.conversations(),
    );
    const match = cached?.conversations.find((c) => c.id === conversationId);
    if (!match) return null;
    return {
      name: getConversationDisplayName(match, currentUserId),
      avatar: getConversationAvatar(match, currentUserId),
    };
  }, [conversation, conversationId, currentUserId, queryClient]);

  // =========================================================================
  // Render
  // =========================================================================

  if (!conversationId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm" style={{ color: 'var(--user-page-text)' }}>{t('messaging.selectConversation')}</p>
      </div>
    );
  }

  // Show skeleton while conversation metadata is loading
  if (!conversation) {
    return (
      <ChatPageSkeleton
        name={preview?.name}
        avatar={preview?.avatar}
        onBack={() => navigate('/messages', { replace: true })}
      />
    );
  }

  // TopBar content: back + avatar + name/status + connection indicator
  const topBarContent = (
    <>
      {/* Back button — mobile only */}
      {isMobile && (
        <button
          type="button"
          onClick={() => isMobile ? navigate('/messages', { replace: true }) : pop()}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-all active:scale-95"
          style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}
        >
          <svg className="h-4 w-4 rtl:rotate-180" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {/* Avatar + Name + Status — tappable block */}
      <button type="button" onClick={handleAvatarTap} className="flex flex-1 flex-col overflow-hidden text-start">
        {/* Row 1: Avatar + Name (centered) */}
        <div className="flex items-center gap-2.5">
          <div className="relative flex-shrink-0">
            <ChatAvatar
              src={displayAvatar}
              name={displayName}
              borderColor="border-slate-300"
              fallbackClass="bg-slate-100 text-slate-700"
            />
            {isOnline && (
              <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 bg-success-500" style={{ borderColor: 'var(--user-page-card-border)' }} />
            )}
          </div>
          <div className="flex flex-1 items-center gap-1 overflow-hidden" dir="auto">
            <span className="truncate text-sm font-semibold" style={{ color: 'var(--user-page-heading)' }}>{displayName}</span>
            {conversation && conversation.type !== 'dm' && (
              <svg className="h-3.5 w-3.5 flex-shrink-0 rtl:rotate-180" style={{ color: 'var(--user-page-text)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
          </div>
        </div>
        {/* Row 2: Status — below avatar, aligned to its left edge */}
        <div className="h-4 text-[11px]" style={{ color: 'var(--user-page-text)' }}>
          {isOnline ? (
            <span className="font-medium text-success-600">{t('messaging.online')}</span>
          ) : lastSeenFormatted ? (
            <span>{t('messaging.lastSeen')}: {lastSeenFormatted}</span>
          ) : conversation && conversation.type !== 'dm' ? (
            <span>{t('messaging.members', { count: conversation.participants.length })}</span>
          ) : null}
        </div>
      </button>

      {/* Search button */}
      <button
        type="button"
        onClick={() => setIsSearchOpen(!isSearchOpen)}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-all active:scale-95"
        style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}
      >
        <svg className="h-4 w-4" style={{ color: 'var(--user-page-heading)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>

      {/* Connection indicator */}
      {!isConnected && (
        <div className="flex items-center gap-1.5 rounded-full px-2.5 py-1 backdrop-blur-sm" style={{ backgroundColor: 'var(--user-page-heading)' }}>
          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
          <span className="text-[10px] font-medium text-white/90">
            {t('messaging.connecting')}
          </span>
        </div>
      )}
    </>
  );

  return (
    <div style={{ backgroundColor: 'var(--user-page-card-bg)' }} className="h-full">
    <PageLayout
      topBar={topBarContent}
      contentClassName="relative flex flex-1 flex-col overflow-hidden"
    >
      {/* Neural topography background — gradient comes from AppLayout */}
      <VidnightBackground />

      {/* Search overlay */}
      {isSearchOpen && conversationId && (
        <MessageSearch
          conversationId={conversationId}
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          onResultSelect={handleSearchResultSelect}
        />
      )}

      {/* Connection status banner */}
      <ConnectionStatusBanner isConnected={isConnected} />

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto py-2"
        style={{ backgroundColor: 'color-mix(in srgb, var(--user-page-text) 8%, transparent)' }}
        onScroll={handleScroll}
      >
        {/* Load more indicator */}
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <div className="h-5 w-5 animate-spin rounded-full border" style={{ borderColor: 'var(--user-page-card-border)', borderTopColor: 'var(--user-page-heading)' }} />
          </div>
        )}

        {groupedMessages.map(({ msg, isGrouped, showSender, showAvatar }, i) => {
          const prevMsg = i > 0 ? groupedMessages[i - 1].msg : null;
          const showDateSeparator = !prevMsg || !isSameDay(
            new Date(msg.createdAt),
            new Date(prevMsg.createdAt),
          );
          const isLastReadMessage = msg.id === lastReadMessageId;

          return (
            <div key={msg.id}>
              {showDateSeparator && <DateSeparator date={new Date(msg.createdAt)} />}
              <MessageBubble
                message={msg}
                showSender={showSender && conversation?.type !== 'dm'}
                showAvatar={showAvatar}
                isGrouped={isGrouped && !showDateSeparator}
                isHighlighted={highlightedMessageId === msg.id}
                onReply={handleReply}
                onReaction={handleReaction}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onStar={handleStar}
                onRetry={retrySend}
                onRemoveOptimistic={removeOptimistic}
                onLongPress={handleLongPress}
                onMediaTap={handleMediaTap}
                onOpenThread={handleOpenThread}
              />
              {/* Mini read-receipt avatar */}
              {isLastReadMessage && displayAvatar && (
                <div className="flex justify-end pe-3 -mt-0.5 mb-1">
                  <ChatAvatar src={displayAvatar} name={displayName} size="h-3.5 w-3.5" />
                </div>
              )}
            </div>
          );
        })}

        {/* Typing indicator */}
        <TypingIndicator typingUsers={typingUsers} />

        <div ref={messagesEndRef} />
      </div>

      {/* Input — swap for VoiceRecorder when recording */}
      <AnimatePresence mode="wait">
        {isRecordingVoice ? (
          <VoiceRecorder
            key="voice-recorder"
            onRecordingComplete={handleVoiceRecordingComplete}
            onCancel={() => setIsRecordingVoice(false)}
          />
        ) : (
          <MessageInput
            key={`message-input-${conversationId}`}
            onSend={handleSendMessage}
            onTyping={sendTyping}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            disabled={!isConnected}
            onMicPress={() => setIsRecordingVoice(true)}
            onAttachPress={() => setIsMediaPickerOpen(true)}
            isMediaPickerOpen={isMediaPickerOpen}
            onMediaPickerClose={() => setIsMediaPickerOpen(false)}
            onMediaFilesSelected={handleMediaFiles}
            participants={conversation?.participants}
            conversationType={conversation?.type}
            currentUserId={currentUserId}
            conversationId={conversationId}
          />
        )}
      </AnimatePresence>

      {/* Action menu */}
      {actionMenu && (
        <MessageActionMenu
          message={actionMenu.message}
          position={actionMenu.position}
          onClose={() => setActionMenu(null)}
          onReply={handleReply}
          onReaction={handleReaction}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onStar={handleStar}
          onPin={(messageId, action) => sendPin(messageId, action)}
          isAdmin={conversation?.myRole === 'admin'}
          onForward={(msg) => setForwardMessage(msg)}
          onInfo={(msg) => setInfoMessage(msg)}
        />
      )}

      {/* Media Picker is now inside MessageInput's attach button */}

      {/* Forward Message modal */}
      <ForwardMessage
        message={forwardMessage!}
        isOpen={!!forwardMessage}
        onClose={() => setForwardMessage(null)}
        onForward={handleForward}
      />

      {/* Poll Creator */}
      <PollCreator
        isOpen={isPollCreatorOpen}
        onClose={() => setIsPollCreatorOpen(false)}
        onCreatePoll={handleCreatePoll}
      />

      {/* Message Info Modal */}
      <MessageInfoModal
        message={infoMessage}
        isOpen={!!infoMessage}
        onClose={() => setInfoMessage(null)}
        participants={conversation?.participants}
      />

      {/* User Profile Sheet */}
      <UserProfileSheet
        user={profileUser}
        isOpen={!!profileUser}
        onClose={() => setProfileUser(null)}
        onViewProfile={(userId) => setViewProfileUserId(userId)}
      />

      {/* Full User Profile Modal */}
      <UserProfileModal
        userId={viewProfileUserId}
        isOpen={!!viewProfileUserId}
        onClose={() => setViewProfileUserId(null)}
      />

      {/* Media Viewer Lightbox */}
      <MediaViewer
        items={mediaGallery}
        initialIndex={viewerIndex}
        isOpen={viewerIndex >= 0}
        onClose={() => setViewerIndex(-1)}
      />

      {/* Thread Panel (desktop only — mobile uses ThreadActivity) */}
      {!isMobile && activeThread && conversationId && (
        <ThreadPanel
          conversationId={conversationId}
          rootMessage={activeThread}
          onClose={() => setActiveThread(null)}
        />
      )}
    </PageLayout>
    </div>
  );
}

export default ChatPage;
